import React, { useState, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  ChevronLeft, ChevronRight, Plus, Video, Camera, PenLine, Loader2, ScanLine
} from 'lucide-react';
import WhiteboardScanModal from '@/components/WhiteboardScanModal';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isToday, isSameMonth, parseISO
} from 'date-fns';
import { it } from 'date-fns/locale';

const QUICK_TITLES = [
  { label: '📹 Video Shooting', icon: Video },
  { label: '📸 Fotografia', icon: Camera },
  { label: '✍️ Copy', icon: PenLine },
];

const STATUS_COLORS = {
  pending:     'bg-zinc-700 border-zinc-600 text-zinc-200',
  in_progress: 'bg-amber-500/20 border-amber-500/40 text-amber-300',
  waiting:     'bg-blue-500/20 border-blue-500/40 text-blue-300',
  completed:   'bg-green-500/20 border-green-500/40 text-green-300',
};

const TaskChip = React.forwardRef(({ task, dragHandleProps, draggableProps, innerRef, isDragging }, _ref) => (
  <div
    ref={innerRef}
    {...draggableProps}
    {...dragHandleProps}
    className={cn(
      'text-[11px] px-2 py-1 rounded border truncate cursor-grab select-none transition-shadow',
      STATUS_COLORS[task.status] || STATUS_COLORS.pending,
      isDragging && 'shadow-lg opacity-80'
    )}
    title={`${task.title} → ${task.assigned_to_name}`}
  >
    <span className="font-semibold">{task.title}</span>
    <span className="text-[9px] opacity-60 ml-1">· {task.assigned_to_name}</span>
  </div>
));
TaskChip.displayName = 'TaskChip';

const ScheduleCalendar = ({ tasks, members, user, apiUpdateTask, createTask }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [quickCreateDay, setQuickCreateDay] = useState(null); // Date object
  const [newTitle, setNewTitle] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  // ── Derived data ──────────────────────────────────────────────
  const backlogTasks = useMemo(
    () => tasks.filter(t => !t.due_date && t.status !== 'completed'),
    [tasks]
  );

  const monthTasks = useMemo(() => {
    const monthStart = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const monthEnd   = format(endOfMonth(currentMonth),   'yyyy-MM-dd');
    return tasks.filter(
      t => t.due_date && t.due_date >= monthStart && t.due_date <= monthEnd && t.status !== 'completed'
    );
  }, [tasks, currentMonth]);

  const getTasksForDay = (dateStr) => monthTasks.filter(t => t.due_date === dateStr);

  // ── Month grid ────────────────────────────────────────────────
  const days = useMemo(() => {
    return eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  }, [currentMonth]);

  const startingDayIndex = useMemo(() => {
    const firstDay = startOfMonth(currentMonth);
    return (firstDay.getDay() + 6) % 7; // Monday = 0
  }, [currentMonth]);

  // ── Drag & drop ───────────────────────────────────────────────
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    if (destination.droppableId === 'backlog') {
      await apiUpdateTask(draggableId, { due_date: null });
    } else if (destination.droppableId.startsWith('day-')) {
      const dateStr = destination.droppableId.replace('day-', '');
      await apiUpdateTask(draggableId, { due_date: dateStr });
    }
  };

  // ── Quick create ──────────────────────────────────────────────
  const openQuickCreate = (day) => {
    setQuickCreateDay(day);
    setNewTitle('');
    setSelectedMembers([]);
  };

  const toggleMember = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    );
  };

  const handleCreate = async () => {
    if (!newTitle.trim() || selectedMembers.length === 0) return;
    setCreating(true);
    const dateStr = format(quickCreateDay, 'yyyy-MM-dd');
    await createTask({
      title: newTitle.trim(),
      assigned_to_ids: selectedMembers,
      assigned_by: user.id,
      assigned_by_name: user.name,
      due_date: dateStr,
    });
    setQuickCreateDay(null);
    setCreating(false);
  };

  const weekDays = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 min-h-[600px]">

        {/* ── Backlog sidebar ─────────────────────────────── */}
        <div className="w-56 flex-shrink-0 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-white uppercase tracking-wider text-sm">Backlog</h3>
            <Badge className="bg-zinc-800 text-zinc-400">{backlogTasks.length}</Badge>
          </div>
          <Droppable droppableId="backlog">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={cn(
                  'flex-1 flex flex-col gap-1.5 p-2 rounded-xl border min-h-[200px] transition-colors',
                  snapshot.isDraggingOver
                    ? 'border-amber-500/40 bg-amber-500/5'
                    : 'border-zinc-800 bg-zinc-900/50'
                )}
              >
                {backlogTasks.length === 0 && !snapshot.isDraggingOver && (
                  <p className="text-xs text-zinc-600 text-center pt-4 italic">Nessun ordine da pianificare</p>
                )}
                {backlogTasks.map((task, index) => (
                  <Draggable key={task.id} draggableId={task.id} index={index}>
                    {(provided, snapshot) => (
                      <TaskChip
                        task={task}
                        innerRef={provided.innerRef}
                        draggableProps={provided.draggableProps}
                        dragHandleProps={provided.dragHandleProps}
                        isDragging={snapshot.isDragging}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <p className="text-[10px] text-zinc-600 text-center italic">
            Trascina un ordine sul calendario per pianificarlo
          </p>
        </div>

        {/* ── Calendar ────────────────────────────────────── */}
        <div className="flex-1 flex flex-col gap-3">
          {/* Month navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              className="border-zinc-700 text-zinc-400"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h3 className="font-display text-xl font-bold text-white capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: it })}
            </h3>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setScanOpen(true)}
                className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 hover:text-amber-400 text-xs gap-1.5"
              >
                <ScanLine className="w-3.5 h-3.5" />
                Scansiona
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                className="border-zinc-700 text-zinc-400"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Week day headers */}
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map(d => (
              <div key={d} className="text-center text-xs font-bold text-zinc-500 uppercase py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1 flex-1">
            {/* Empty offset cells */}
            {Array.from({ length: startingDayIndex }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}

            {/* Day cells */}
            {days.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayTasks = getTasksForDay(dateStr);
              const overflow = dayTasks.length > 3;
              const visibleTasks = overflow ? dayTasks.slice(0, 3) : dayTasks;
              const today = isToday(day);

              return (
                <Droppable key={dateStr} droppableId={`day-${dateStr}`}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={cn(
                        'relative group min-h-[90px] p-1.5 rounded-lg border flex flex-col gap-1 transition-colors',
                        today
                          ? 'border-amber-500/60 bg-amber-500/5'
                          : 'border-zinc-800 bg-zinc-900/30',
                        snapshot.isDraggingOver && 'border-amber-400/60 bg-amber-400/10'
                      )}
                    >
                      {/* Day number + quick-add button */}
                      <div className="flex items-center justify-between">
                        <span className={cn(
                          'text-xs font-bold',
                          today ? 'text-amber-400' : 'text-zinc-500'
                        )}>
                          {format(day, 'd')}
                        </span>
                        <button
                          onClick={() => openQuickCreate(day)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity w-4 h-4 flex items-center justify-center rounded text-zinc-500 hover:text-amber-400 hover:bg-zinc-800"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Task chips */}
                      {visibleTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <TaskChip
                              task={task}
                              innerRef={provided.innerRef}
                              draggableProps={provided.draggableProps}
                              dragHandleProps={provided.dragHandleProps}
                              isDragging={snapshot.isDragging}
                            />
                          )}
                        </Draggable>
                      ))}

                      {overflow && (
                        <span className="text-[10px] text-zinc-500 pl-1">
                          +{dayTasks.length - 3} altri
                        </span>
                      )}

                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Quick-create dialog ──────────────────────────── */}
      <Dialog open={!!quickCreateDay} onOpenChange={() => setQuickCreateDay(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-white text-lg">
              Nuovo Ordine
            </DialogTitle>
            {quickCreateDay && (
              <DialogDescription className="text-zinc-500 text-xs capitalize">
                {format(quickCreateDay, 'EEEE d MMMM yyyy', { locale: it })}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Quick-fill presets */}
            <div className="flex gap-2 flex-wrap">
              {QUICK_TITLES.map(({ label }) => (
                <button
                  key={label}
                  onClick={() => setNewTitle(label)}
                  className={cn(
                    'text-[11px] px-2.5 py-1 rounded-full border transition-all font-medium',
                    newTitle === label
                      ? 'border-amber-500/60 bg-amber-500/10 text-amber-300'
                      : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Title input */}
            <Input
              placeholder="Titolo ordine..."
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              autoFocus
            />

            {/* Member selection */}
            <div>
              <p className="text-xs text-zinc-500 mb-2 font-bold uppercase tracking-wider">Assegna a</p>
              <div className="flex flex-wrap gap-2">
                {members.filter(m => m.role !== 'admin').map(m => (
                  <button
                    key={m.id}
                    onClick={() => toggleMember(m.id)}
                    className={cn(
                      'text-xs px-3 py-1.5 rounded-full border transition-all font-medium',
                      selectedMembers.includes(m.id)
                        ? 'border-amber-500/60 bg-amber-500/10 text-amber-300'
                        : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                    )}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-400"
                onClick={() => setQuickCreateDay(null)}
              >
                Annulla
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-black font-bold"
                onClick={handleCreate}
                disabled={creating || !newTitle.trim() || selectedMembers.length === 0}
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'CREA ORDINE'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <WhiteboardScanModal
        isOpen={scanOpen}
        onClose={() => setScanOpen(false)}
        members={members}
        user={user}
        createTask={createTask}
      />
    </DragDropContext>
  );
};

export default ScheduleCalendar;
